import * as React from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    IconButton,
    InputAdornment,
    Link,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GitHubIcon from "@mui/icons-material/GitHub";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { MailOutline } from "@mui/icons-material";

type FormState = {
    name: string;
    email: string;
    message: string;
};

const ACCENT = "#2766a8";
const BORDER = "1px solid rgba(17, 24, 39, 0.10)";
const DIVIDER = "1px solid rgba(17, 24, 39, 0.10)";

export default function ContactLandingPage(): JSX.Element {
    const [form, setForm] = React.useState<FormState>({
        name: "",
        email: "",
        message: "",
    });

    const isValid =
        form.name.trim().length > 0 &&
        form.email.trim().length > 0 &&
        form.message.trim().length > 0;

    const onChange =
        (key: keyof FormState) =>
            (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setForm((prev) => ({ ...prev, [key]: e.target.value }));
            };

    const handleSendMessage = () => {
        const to = "hello@aggreylelei.com";

        const subject = encodeURIComponent(`Message from ${form.name.trim()}`);

        const body = encodeURIComponent(
            `Name: ${form.name.trim()}\n` +
            `Email: ${form.email.trim()}\n\n` +
            `Message:\n${form.message.trim()}`
        );

        window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
    };

    return (
        <Box
            id="contact-section"
            sx={{
                bgcolor: "#fafbfc",
                minHeight: "100vh",
                scrollMarginTop: "60px",
            }}
        >
            <Container maxWidth="lg" sx={{ py: { xs: 1, md: 3 } }}>
                {/* Hero */}
                <Box sx={{ textAlign: "center", mb: { xs: 2, md: 4 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        Cont
                        <Box component="span" sx={{ color: ACCENT }}>
                            act
                        </Box>
                    </Typography>

                    <Typography
                        variant="h5"
                        sx={{
                            color: "#4a5565 !important",
                            maxWidth: 760,
                            mx: "auto",
                        }}
                    >
                        Question, idea, or just want to say hi?
                    </Typography>
                </Box>

                {/* ONE merged card */}
                <Card
                    elevation={0}
                    sx={{
                        border: BORDER,
                        borderRadius: 2,
                    }}
                >
                    <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                        <Grid container spacing={0} alignItems="stretch">
                            {/* Left: Contact form */}
                            <Grid item xs={12} md={7}>
                                <Box sx={{ pr: { md: 3 } }}>
                                    <Typography variant="h6" sx={{ fontWeight: 750 }}>
                                        Send a Message
                                    </Typography>

                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 450, color: "#2766A8B5" }}
                                    >
                                        hello@aggreylelei.com
                                    </Typography>

                                    <Box
                                        component="form"
                                        sx={{ mt: 3 }}
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: "rgba(17,24,39,0.75)",
                                                    }}
                                                >
                                                    Name *
                                                </Typography>

                                                <TextField
                                                    fullWidth
                                                    size="medium"
                                                    placeholder="Foo Bar"
                                                    value={form.name}
                                                    onChange={onChange("name")}
                                                    sx={{ mt: 0.75, borderRadius: 3 }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <PersonOutlineRoundedIcon
                                                                    sx={{
                                                                        color: "rgba(17, 24, 39, 0.45)",
                                                                    }}
                                                                    fontSize="small"
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: "rgba(17,24,39,0.75)",
                                                    }}
                                                >
                                                    Email *
                                                </Typography>

                                                <TextField
                                                    fullWidth
                                                    size="medium"
                                                    placeholder="foo@bar.com"
                                                    value={form.email}
                                                    onChange={onChange("email")}
                                                    sx={{ mt: 0.75 }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <EmailOutlinedIcon
                                                                    sx={{
                                                                        color: "rgba(17, 24, 39, 0.45)",
                                                                    }}
                                                                    fontSize="small"
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: "rgba(17,24,39,0.75)",
                                                    }}
                                                >
                                                    Message *
                                                </Typography>

                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    minRows={6}
                                                    placeholder="What are you interested in?"
                                                    value={form.message}
                                                    onChange={onChange("message")}
                                                    sx={{
                                                        mt: 0.75,
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: 2,
                                                        },
                                                        "& .MuiOutlinedInput-notchedOutline": {
                                                            borderColor: "rgba(17, 24, 39, 0.18)",
                                                        },
                                                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor: ACCENT,
                                                                borderWidth: 2,
                                                            },
                                                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor: "rgba(17, 24, 39, 0.25)",
                                                            },
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Stack
                                                    direction="row"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    sx={{ mt: 0.5 }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: "rgba(17, 24, 39, 0.55)",
                                                        }}
                                                    >
                                                        * Required fields
                                                    </Typography>

                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        disabled={!isValid}
                                                        endIcon={<MailOutline fontSize="small" />}
                                                        sx={{
                                                            borderRadius: 2,
                                                            textTransform: "none",
                                                            fontWeight: 700,
                                                            px: 2.25,
                                                            bgcolor: ACCENT,
                                                            boxShadow: "none",
                                                            color: "#fff !important",
                                                            "&:hover": {
                                                                bgcolor: "#2766a8",
                                                                boxShadow: "none",
                                                            },
                                                            "&.Mui-disabled": {
                                                                bgcolor: "rgba(39,102,168,0.71)",
                                                                color: "rgba(255,255,255,0.9) !important",
                                                            },
                                                        }}
                                                    >
                                                        Send Message
                                                    </Button>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Right: Info cards */}
                            <Grid item xs={12} md={5}>
                                <Box
                                    sx={{
                                        pl: { md: 3 },
                                        pt: { xs: 3, md: 0 },
                                        borderLeft: { xs: "none", md: DIVIDER },
                                        borderTop: { xs: DIVIDER, md: "none" },
                                        height: "100%",
                                        display: "flex",
                                        alignItems: { xs: "stretch", md: "center" },
                                    }}
                                >
                                    <Box sx={{ width: "100%" }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                            Other Ways to Connect
                                        </Typography>

                                        <Stack spacing={1.5} sx={{ mt: 2 }}>
                                            {[
                                                {
                                                    label: "LinkedIn",
                                                    icon: <LinkedInIcon sx={{ color: "#0A66C2" }} />,
                                                    href: "https://www.linkedin.com/in/aggrey-lelei",
                                                },
                                                {
                                                    label: "GitHub",
                                                    icon: <GitHubIcon sx={{ color: "#111827" }} />,
                                                    href: "https://github.com/hashedgrey/",
                                                },
                                            ].map((row) => (
                                                <Box
                                                    key={row.label}
                                                    sx={{
                                                        border: "1px solid rgba(17, 24, 39, 0.10)",
                                                        borderRadius: 2,
                                                        px: 1.75,
                                                        py: 1.25,
                                                    }}
                                                >
                                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                                        <Box
                                                            sx={{
                                                                width: 34,
                                                                height: 34,
                                                                borderRadius: 1.5,
                                                                border: "1px solid rgba(17, 24, 39, 0.12)",
                                                                display: "grid",
                                                                placeItems: "center",
                                                                flexShrink: 0,
                                                                bgcolor: "#fff",
                                                            }}
                                                        >
                                                            {row.icon}
                                                        </Box>

                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                                                {row.label}
                                                            </Typography>
                                                        </Box>

                                                        <IconButton
                                                            target="_blank"
                                                            size="small"
                                                            component={Link}
                                                            href={row.href}
                                                            underline="none"
                                                            sx={{
                                                                borderRadius: 1.5,
                                                                border: "1px solid rgba(17, 24, 39, 0.12)",
                                                            }}
                                                            aria-label={`Open ${row.label}`}
                                                        >
                                                            <OpenInNewRoundedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
}